import './update-trade.scss'

import { Button, notification, Popconfirm } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { DatePicker, Form, FormItem, Input, InputNumber, Select, SubmitButton } from 'formik-antd'
import React, { useEffect, useState } from 'react'
import { Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetailByDomain } from '../../../api'
import { deleteTrade as deleteTradeApi } from '../../../api/delete-trade'
import { InputCurrency } from '../../../components'
import { emptyCharacter } from '../../../components/EmptyChar'
import { filterProps, parseApiErrorResponse } from '../../../components/forms/utils'
import { config } from '../../../config'
import { formatCurrency, toFixed } from '../../../formatters'
import { getInterestPaidCodes, InterestPaid, isDiscountedSecurity } from '../../../models'
import { convertEntityValuesToFormValues } from '../../../util/convert-entity-values-to-form-values'
import { httpClient } from '../../../util/http-client'
import { messages } from '../messages'
import {
  convertTradeUpdateFormValuesToUpdateTradePayload,
  Trade,
  updateTradeFormInitialValues,
  UpdateTradeFormValues,
} from '../models'
import { tradeValidationSchema } from './validators'

const interestPaidCodes = getInterestPaidCodes()

interface UpdateTradeProps {
  tradeId: number
  domain: string
  onSuccess: () => void
}

export const UpdateTrade = ({ tradeId, domain, onSuccess }: UpdateTradeProps) => {
  const [tradeFormValues, setTradeFormValues] = useState<UpdateTradeFormValues>(updateTradeFormInitialValues)

  const { data: trade, error } = useSWR<Trade>(tradeId ? [`/trades/${tradeId}`, domain] : null, fetchDetailByDomain, {
    suspense: true,
  })

  useEffect(() => {
    if (!trade) return
    trade.principal = +toFixed({ value: trade.principal, precision: 2 })
    setTradeFormValues(convertEntityValuesToFormValues(updateTradeFormInitialValues, trade))
  }, [trade])

  if (error) return <Row>{`Could not load trade ID ${tradeId}.`}</Row>

  const onSubmit = async (
    tradeUpdateFormValues: UpdateTradeFormValues,
    { setErrors, setSubmitting }: FormikHelpers<UpdateTradeFormValues>,
  ) => {
    try {
      await httpClient(`/trades/${tradeId}`, {
        method: 'PUT',
        domain,
        data: convertTradeUpdateFormValuesToUpdateTradePayload(tradeUpdateFormValues),
      })

      notification.success({ message: messages.common.success, description: messages.trade.update.success })

      onSuccess()
    } catch (error) {
      setErrors(parseApiErrorResponse(error))

      notification.error({ message: messages.common.error, description: messages.trade.update.error })
    } finally {
      setSubmitting(false)
    }
  }

  const deleteTrade = async () => {
    try {
      await deleteTradeApi({ tradeId, domain })
      notification.success({ message: messages.common.success, description: messages.trade.delete.success })
      onSuccess()
    } catch (error) {
      notification.error({ message: messages.common.error, description: error.error_message })
    }
  }

  return (
    <Formik
      initialValues={tradeFormValues}
      validationSchema={tradeValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ submitForm }) => (
        <Form name="update-trade">
          <Row justifyContentSpaceBetween className="trade-details">
            <dl>
              <dt>ID</dt>
              <dd>{trade?.id}</dd>
            </dl>
            <dl>
              <dt>Instrument</dt>
              <dd>{trade?.instrumentCode}</dd>
            </dl>
            <dl>
              <dt>Issuer</dt>
              <dd>
                {trade?.issuer?.name} ({trade?.issuer?.code})
              </dd>
            </dl>
            <dl>
              <dt>Principal ($)</dt>
              <dd>{formatCurrency({ value: trade?.principal })}</dd>
            </dl>
            <dl>
              <dt>Coupon</dt>
              <dd>{toFixed({ value: trade?.coupon })}</dd>
            </dl>
            <dl>
              <dt>Source</dt>
              <dd>{trade?.source || emptyCharacter}</dd>
            </dl>
          </Row>

          {isDiscountedSecurity(trade?.instrumentCode) && (
            <FormItem label="Notional Principal $" name="notionalPrincipal" required>
              <InputCurrency name="notionalPrincipal" aria-label="notionalPrincipal" min={0} />
            </FormItem>
          )}

          <FormItem label="Principal $" name="principal" required>
            <InputCurrency
              name="principal"
              aria-label="principal"
              min={0}
              disabled={isDiscountedSecurity(trade?.instrumentCode)}
            />
          </FormItem>

          <FormItem label="Coupon" name="coupon" required>
            <InputNumber name="coupon" aria-label="coupon" min={0} />
          </FormItem>

          <FormItem label="Allocation Code" name="allocationCode" required>
            <Input name="allocationCode" aria-label="allocationCode" />
          </FormItem>

          <FormItem label="Purchase Date" name="purchaseDate" required>
            <DatePicker
              name="purchaseDate"
              aria-label="purchaseDate"
              format={config.defaultDateFormat}
              allowClear={false}
            />
          </FormItem>

          <FormItem label="Maturity Date" name="maturityDate" required>
            <DatePicker
              name="maturityDate"
              aria-label="maturityDate"
              format={config.defaultDateFormat}
              allowClear={false}
            />
          </FormItem>

          <FormItem name="interestPaidCode" label="Interest Paid" required>
            <Select
              name="interestPaidCode"
              placeholder="Select the interest paid"
              showSearch
              options={Object.keys(interestPaidCodes).map((interestPaidCode: InterestPaid) => ({
                label: interestPaidCodes[interestPaidCode],
                value: interestPaidCode,
              }))}
              {...filterProps}
            />
          </FormItem>

          <FormItem label="Reference" name="reference">
            <Input name="reference" aria-label="reference" />
          </FormItem>

          <FormItem label="Bank Reference" name="bankReference">
            <Input name="bankReference" aria-label="bankReference" />
          </FormItem>

          <FormItem label="Note" name="note">
            <Input name="note" aria-label="note" />
          </FormItem>

          <FormItem name="actions">
            <Row className="action-buttons">
              <Popconfirm title="Are you sure you want to delete this trade?" onConfirm={deleteTrade}>
                <Button danger>Delete</Button>
              </Popconfirm>
              <Popconfirm title="Are you sure you want to update this trade?" onConfirm={submitForm}>
                <SubmitButton className="update-trade" htmlType="submit" type="primary">
                  Update Trade
                </SubmitButton>
              </Popconfirm>
            </Row>
          </FormItem>
        </Form>
      )}
    </Formik>
  )
}
